import type {
  BusinessEdge,
  BusinessFlow,
  BusinessNode,
  BusinessNodeKind,
  BusinessSection,
  BusinessSource,
  BusinessText,
} from './business-logic-types.js';

// Reviewed documentation of the Navigate/reference-first source, not a live graph,
// deployment detector, editable policy or permission to execute a call action.
export const BUSINESS_LOGIC_VERSION = 'Reviewed Navigate implementation · 2026-10-27 · not a live runtime graph';
export const BUSINESS_MATCHING_RULES = {
  name: 0.8,
  surname: 0.85,
  email: 0.9,
  phoneticFloor: 0.65,
  weakPhoneticCeiling: 0.85,
  failedChecks: 3,
} as const;

// Eligibility only: every capability retains the account, evidence, feature-flag,
// uniqueness and freshness prerequisites explained in the displayed sections.
// A/B describe incoming-phone routes; a different spoken number is the C21 route.
export const BUSINESS_FLOW_RULES = {
  a: {
    namesOnlyEligible: true,
    spokenNamesOnly: false,
    weakSurnameRetry: true,
    emailJudge: true,
    creationOnly: false,
    closedFollowup: false,
  },
  b: {
    namesOnlyEligible: true,
    spokenNamesOnly: false,
    weakSurnameRetry: true,
    emailJudge: true,
    creationOnly: false,
    closedFollowup: false,
  },
  c21: {
    namesOnlyEligible: true,
    spokenNamesOnly: true,
    weakSurnameRetry: true,
    emailJudge: true,
    creationOnly: false,
    closedFollowup: false,
  },
  c22: {
    namesOnlyEligible: false,
    spokenNamesOnly: false,
    weakSurnameRetry: false,
    emailJudge: false,
    creationOnly: true,
    closedFollowup: false,
  },
  existing: {
    namesOnlyEligible: true,
    spokenNamesOnly: true,
    weakSurnameRetry: false,
    emailJudge: false,
    creationOnly: false,
    closedFollowup: true,
  },
  'closed-new': {
    namesOnlyEligible: false,
    spokenNamesOnly: false,
    weakSurnameRetry: false,
    emailJudge: false,
    creationOnly: false,
    closedFollowup: true,
  },
  callback: {
    namesOnlyEligible: true,
    spokenNamesOnly: false,
    weakSurnameRetry: false,
    emailJudge: false,
    creationOnly: false,
    closedFollowup: false,
  },
  technician: {
    namesOnlyEligible: false,
    spokenNamesOnly: false,
    weakSurnameRetry: false,
    emailJudge: false,
    creationOnly: false,
    closedFollowup: false,
  },
} as const;

type FlowId = keyof typeof BUSINESS_FLOW_RULES;
const t = (de: string, en: string): BusinessText => ({ de, en });
const section = (de: string, en: string, ...paragraphs: BusinessText[]): BusinessSection => ({
  title: t(de, en),
  paragraphs,
});
// Paths are relative to entrypoints/zwickroell/src/.
const src = (file: string, label: string): BusinessSource => ({ file, label });
const sources = {
  reference: src('server/reference-first.ts', 'Reference scope, name pairs and identity completion'),
  prompts: src('server/reference-first-prompts.ts', 'Reference-first stages and spoken guidance'),
  matching: src('db/verification-matchers.ts', 'Name, email and phone matching primitives'),
  tools: src('db/vapi-tools.ts', 'CRM lookup, capture, ticket tools and receipts'),
  people: src('server/twilio-account-match.ts', 'Same CRM person and customer email selection'),
  attempts: src('server/reference-verification-input.ts', 'Caller-exchange evidence and failure deduplication'),
  email: src('server/email-verification.ts', 'Conditional parallel email verification'),
  emailRetry: src('server/reference-email-retry.ts', 'New-dossier email retry eligibility and choice'),
  capture: src('server/contact-capture-ledger.ts', 'Creation-only field preparation and confirmation'),
  reuse: src('server/crm-ticket-policy.ts', 'Contact reuse, collision and account checks'),
  closed: src('server/closed-ticket-followup.ts', 'Closed-case successor preparation and confirmation'),
  collection: src('server/ticket-collection.ts', 'Issue, machine state, availability and history boundary'),
  runtime: src('server/navigate-runtime.ts', 'Navigate workflow instructions and closed-case overlay'),
  provider: src('server/call-llm-provider.ts', 'Incoming lookup, status classification and runtime facts'),
  engineer: src('server/engineer-ticket.ts', 'Registered engineer permission and customer selection'),
};
const node = (
  id: string,
  kind: BusinessNodeKind,
  column: number,
  row: number,
  title: BusinessText,
  summary: BusinessText,
  sections: BusinessSection[],
  refs: BusinessSource[],
): BusinessNode => ({ id, kind, column, row, title, summary, sections, sources: refs });
const edge = (from: string, to: string, de?: string, en?: string): BusinessEdge => ({
  id: `${from}--${to}`,
  from,
  to,
  ...(de && en ? { label: t(de, en) } : {}),
});
const R = BUSINESS_MATCHING_RULES;
const commonNote = t(
  'Lesbare Übersicht der geprüften Navigate-Logik. Quellenstand und aktive Konfiguration entscheiden; diese Ansicht führt keine Anrufe oder CRM-Aktionen aus.',
  'Readable overview of reviewed Navigate logic. Source version and active configuration matter; this view executes no calls or CRM actions.',
);
const terminalNote = t(
  'Ein ausdrücklicher Wunsch nach einem Mitarbeiter oder Gesprächsende bleibt in jeder Phase möglich. Die bestehenden Transfer- und Endwerkzeuge bleiben unverändert.',
  'An explicit request for a colleague or to end the call remains possible at every stage. Existing transfer and end tools are unchanged.',
);

function capabilitySection(id: FlowId): BusinessSection {
  const rule = BUSINESS_FLOW_RULES[id];
  return section(
    'Geltungsbereich dieser Route',
    'Capabilities of this route',
    t(
      rule.creationOnly
        ? 'Diese Route bereitet ausschließlich ein neues Ticket vor. Sie authentifiziert keinen Zugriff auf bestehende CAS.'
        : id === 'closed-new'
          ? 'Die bereits geprüfte Identität wird übernommen. Es findet keine neue Namen- oder E-Mail-Authentifizierung statt.'
          : id === 'technician'
            ? 'Eigene Technikerberechtigung und Auswahl der Kundenkontaktperson sind getrennt; dies ist keine normale Kundenauthentifizierung.'
            : rule.namesOnlyEligible
              ? 'Ein passender Telefonbeleg und die vollständigen Namen können E-Mail ersetzen, wenn sämtliche Identitätsbedingungen erfüllt sind.'
              : 'Die Namen allein erteilen in dieser Route keinen Zugriff.',
      rule.creationOnly
        ? 'This route prepares a new ticket only. It does not authenticate access to existing cases.'
        : id === 'closed-new'
          ? 'The already verified identity is inherited. There is no new name or email authentication.'
          : id === 'technician'
            ? 'Engineer permission and customer-contact selection are separate; this is not ordinary customer authentication.'
            : rule.namesOnlyEligible
              ? 'Matching phone evidence and complete names can replace email when every identity condition is satisfied.'
              : 'Names alone do not grant access on this route.',
    ),
    ...(rule.spokenNamesOnly
      ? [
          t(
            'Auch ein passendes angesagtes Telefon ist für die Abkürzung zugelassen; im bestehenden CAS nur in Navigate ohne Rückrufmodus.',
            'A matching spoken phone is also eligible for the shortcut; on an existing CAS, only in Navigate without return-call mode.',
          ),
        ]
      : id === 'callback'
        ? [
            t(
              'Die neue Abkürzung für angesagte Telefonnummern gilt hier nicht. Ein passendes eingehendes Telefon kann weiterhin genügen.',
              'The new spoken-phone shortcut does not apply here. Matching incoming-phone evidence can still suffice.',
            ),
          ]
        : []),
    t(
      rule.weakSurnameRetry
        ? 'Eine zusätzliche Buchstabierchance für einen schwachen phonetischen Nachnamen ist bedingt möglich; die genauen Regeln stehen beim Buchstabierschritt.'
        : 'Die besondere zusätzliche Buchstabierchance der bekannten neuen Dossier-Routen gilt hier nicht. Normale Korrekturen bleiben nach den Regeln dieser Route möglich.',
      rule.weakSurnameRetry
        ? 'An extra spelling opportunity for a weak phonetic surname is conditionally available; see the spelling step for the exact guards.'
        : 'The special extra spelling opportunity for known new-dossier routes does not apply here. Ordinary corrections remain subject to this route’s rules.',
    ),
    t(
      rule.emailJudge
        ? 'Der zusätzliche KI-E-Mail-Prüfer ist nur mit aktivierter Funktion und zulässigem bekannten Dossier-Kontakt verfügbar.'
        : 'Der zusätzliche KI-E-Mail-Prüfer der bekannten neuen Dossier-Routen ist hier nicht aktiv.',
      rule.emailJudge
        ? 'The additional AI email verifier is available only with its feature enabled and an eligible known dossier contact.'
        : 'The additional AI email verifier for known new-dossier routes is not active here.',
    ),
    t(
      rule.closedFollowup
        ? 'Der Übergang vom geschlossenen CAS zu einem neuen Ticket erfordert die gesonderten Voraussetzungen der Closed → New-Ansicht.'
        : 'Diese Route enthält keinen Übergang von einem geschlossenen CAS zu einem neuen Ticket.',
      rule.closedFollowup
        ? 'A closed-CAS-to-new-ticket transition requires the separate prerequisites in the Closed → New view.'
        : 'This route has no closed-CAS-to-new-ticket transition.',
    ),
  );
}

const matcherSection = section(
  'Namen: Vergleich und Grenzwerte',
  'Names: comparison and thresholds',
  t(
    `Vollständiger Vorname: Jaro–Winkler ≥ ${R.name.toFixed(2)}. Vollständiger Nachname: ≥ ${R.surname.toFixed(2)}. Normalisierung: Kleinschreibung, ü→ue, ä→ae, ö→oe, ß→ss und äußere Leerzeichen.`,
    `Complete first name: Jaro–Winkler ≥ ${R.name.toFixed(2)}. Complete surname: ≥ ${R.surname.toFixed(2)}. Normalization: lowercase, ü→ue, ä→ae, ö→oe, ß→ss and outer whitespace.`,
  ),
  t(
    `Gleicher Cologne-Phonetikcode und ursprünglicher Jaro–Winkler-Wert ≥ ${R.phoneticFloor.toFixed(2)} können einen Namen akzeptieren; der Ergebniswert beträgt dann mindestens ${R.weakPhoneticCeiling.toFixed(2)}. Die Werte sind Ähnlichkeitswerte, keine Identitätswahrscheinlichkeiten.`,
    `The same Cologne phonetic code and raw Jaro–Winkler ≥ ${R.phoneticFloor.toFixed(2)} can admit a name; the reported score is then at least ${R.weakPhoneticCeiling.toFixed(2)}. These are similarity scores, not identity probabilities.`,
  ),
  t(
    'Bei normalen Kunden ist ein akzeptierter Nachname mit phoneticBoost und Ergebniswert ≤ 0.85 noch zu schwach für den Weg ohne E-Mail. Ein höherer Wert kann ausreichend stark sein. Die weiteren Identitätsbedingungen bleiben erforderlich.',
    'For ordinary customers, an accepted surname with phoneticBoost and score ≤ 0.85 is still too weak for the email-free route. A higher score can be strong enough. All other identity conditions still apply.',
  ),
);
const pairSection = section(
  'Zwei vollständige Felder, eine Person',
  'Two complete fields, one person',
  t(
    'Der erste genannte Name bleibt vorläufig. Beide vollständigen Felder müssen zur selben CRM-Kontaktperson der ausgewählten Firma passen; Vorname von Person A und Nachname von Person B reichen nicht.',
    'The first supplied name remains provisional. Both complete fields must match the same CRM contact in the selected company; person A’s first name plus person B’s surname is insufficient.',
  ),
  t(
    'Die geeigneten Kundenrouten prüfen auch die umgekehrte Reihenfolge der beiden ganzen Felder. Zusammengesetzte Namen bleiben vollständig; Wörter werden nicht frei aufgeteilt oder neu kombiniert.',
    'Eligible customer routes also test the reverse order of the two whole fields. Compound names remain complete; words are not freely split or recombined.',
  ),
);
const retriesSection = section(
  'Korrekturen und Versuche',
  'Corrections and attempts',
  t(
    `Ohne passende vollständige Namenspaarung bleibt der Agent bei der Korrektur. Der ${R.failedChecks}. gezählte fehlgeschlagene Prüfaustausch desselben Feldes führt zur Übergabe. Zwei falsche Nachnamen führen nicht automatisch zur E-Mail.`,
    `Without a matching complete name pair, the agent stays in correction. The ${R.failedChecks}rd counted failed verification exchange for the same field leads to handoff. Two incorrect surnames do not automatically lead to email.`,
  ),
  t(
    'Die Zähler gelten je Feld; ein erfolgreiches Feld setzt seinen Zähler zurück. Wiederholte Tool-Aufrufe und ergänzte Transkriptfragmente desselben Antwortaustauschs verbrauchen keine zusätzlichen Fehlversuche. Reines Abfragen oder Buchstabieren ist kein fehlgeschlagener Check.',
    'Counters apply per field; success resets that field’s counter. Repeated tool calls and revised transcript fragments in the same answer exchange do not consume additional failures. Merely asking or spelling is not a failed check.',
  ),
);
const shortcutSection = section(
  'Wann darf E-Mail entfallen?',
  'When may email be omitted?',
  t(
    'Die Namen müssen zur selben Person wie ein Telefonkandidat in der aktuellen Firma passen. Die Telefonkandidaten müssen sich auf eine CRM-Identität auflösen lassen, und die Namensreihenfolge muss eindeutig sein oder dieselbe Identität ergeben. Mehrere CRM-Zeilen können dieselbe Person darstellen.',
    'Names must match the same person as a phone candidate in the current company. Phone candidates must resolve to one CRM identity, and name orientation must be unambiguous or resolve to that same identity. Multiple CRM rows can represent one person.',
  ),
  t(
    'Zusätzlich ist ein ausreichend starker Nachname nötig. Eine beliebige Telefonübereinstimmung, ein einzelner passender Name oder ein nur schwach phonetischer Nachname authentifizieren nicht.',
    'A sufficiently strong surname is also required. Any phone match, a single matching name or a merely weak phonetic surname does not authenticate the caller.',
  ),
  t(
    'Passen die Namen, aber diese Bedingungen fehlen, kann E-Mail die passende zulässige Firmenkontaktperson klären – auch wenn diese nicht im Telefon-Trefferset liegt. Unterschiedliche verbleibende Personen werden nicht willkürlich ausgewählt.',
    'If names match but these conditions are missing, email can identify an eligible company contact, including one outside the phone-match set. Distinct remaining people are never chosen arbitrarily.',
  ),
);
const phoneSection = section(
  'Telefon ist ein Beleg, keine Anmeldung',
  'Phone evidence is not authentication',
  t(
    'Die Prüfung erfolgt gegen Telefonnummern der Firma des bestätigten DO/CAS. Ein Treffer in einer anderen Firma autorisiert diesen Vorgang nicht. Eine bekannte eingehende Nummer benötigt eine exakt normalisierte Übereinstimmung.',
    'Matching uses phone numbers in the company of the confirmed DO/CAS. A hit in another company does not authorize this request. A known incoming number requires an exact normalized match.',
  ),
  t(
    'Bei einem tatsächlich angesagten anderen Telefon ist die vorhandene begrenzte STT-Toleranz zulässig: bei zwei +49-Nummern höchstens eine Einfügung, Löschung oder Ersetzung. Dies ist kein allgemeiner unscharfer Telefonvergleich und keine Besitzprüfung.',
    'A genuinely spoken different phone can use the existing limited STT tolerance: at most one insertion, deletion or substitution between two +49 numbers. This is not general fuzzy phone matching or a proof of ownership.',
  ),
);
const referenceSection = section(
  'Referenz vor Identität',
  'Reference before identity',
  t(
    'Eine DO/CAS wird zuerst vollständig vorbereitet, vorgelesen und mit einer echten neuen Antwort bestätigt. Ein falsches Readback muss korrigiert werden; ein früheres Ja zu einer anderen Frage reicht nicht.',
    'A DO/CAS is first prepared, read back and confirmed with a real fresh answer. An incorrect readback must be corrected; an earlier yes to another question is insufficient.',
  ),
  t(
    'Beim Dossier kann die Suche die ursprüngliche Referenz und gegebenenfalls die letzten sechs Ziffern verwenden. Unterschiedliche Maschinentreffer erfordern Klärung; technische Fehler sind kein „nicht gefunden“. Buchstabenpräfixe werden nicht pauschal verworfen.',
    'Dossier lookup can use the original reference and, when applicable, its last six digits. Conflicting machine hits require clarification; technical failures are not “not found”. Letter prefixes are not categorically discarded.',
  ),
);
const companySection = section(
  'Firma ausdrücklich bestätigen',
  'Confirm the company explicitly',
  t(
    'Bei einem neuen Dossier wird die tatsächlich im CRM gefundene Firma genannt und bestätigt, bevor Telefon- oder Identitätsaufnahme fortgesetzt wird. Die DO-Bestätigung bestätigt nicht automatisch die Firma.',
    'For a new dossier, the actual CRM company is named and confirmed before phone or identity capture continues. Confirming the DO does not automatically confirm the company.',
  ),
  t(
    'Eine abweichende oder unklare Antwort führt zu Korrektur/Klärung und gegebenenfalls zur Wahl zwischen korrigierter DO und Mitarbeiter. Unsicherheit allein führt nicht automatisch zur Übergabe.',
    'A different or unclear answer leads to correction/clarification and, when appropriate, a corrected-DO-or-colleague choice. Uncertainty alone does not automatically trigger transfer.',
  ),
);
const weakSection = section(
  'Zusätzliche Chance: nur bedingt',
  'Extra opportunity: conditional only',
  t(
    'Nur bei einem bekannten neuen Dossier (A/B/C21): eine akzeptierte, aber schwache phonetische Nachnamenspaarung kann einmal zum Buchstabieren führen. Voraussetzung: noch kein solcher Marker, kein vorheriger Nachnamenfehler und keine bereits erkannte vollständige Buchstabierung dieser Antwort.',
    'Only for a known new dossier (A/B/C21): an accepted but weak phonetic surname pair can trigger one spelling opportunity. It requires no previous marker, no prior surname failure and no already recognized complete spelling of that answer.',
  ),
  t(
    'Diese besondere Aufforderung verbucht einen Nachnamenfehler. Der nächste Check verwendet dieselben Grenzwerte: stark und sonst ausreichend → ohne E-Mail; weiter akzeptiert, aber schwach → E-Mail; echte Fehlpaarung → normale Fehlerbehandlung.',
    'This special request records one surname failure. The next check uses the same thresholds: strong with sufficient other evidence → no email; still accepted but weak → email; a genuinely mismatching pair → ordinary failure handling.',
  ),
  t(
    'Es gibt keine allgemeine Garantie „immer zwei Nachnamenversuche“. Ein schon buchstabierter Wert, ein vorheriger Fehler oder mehrere Kandidaten können direkt zur erforderlichen E-Mail-Prüfung führen, sofern die Namen überhaupt akzeptiert sind.',
    'There is no universal “always two surname attempts” guarantee. Already spelled input, a prior failure or multiple candidates can lead directly to required email verification, provided the names are accepted at all.',
  ),
);
const basicEmailSection = section(
  'E-Mail: Auswahl unter zulässigen Personen',
  'Email: selection among eligible people',
  t(
    `Nach Strukturprüfung wird die normalisierte ganze Adresse verglichen: exakt oder Jaro–Winkler ≥ ${R.email.toFixed(2)}. Es gibt dabei keine getrennte Domain-Schwelle oder allgemeine Pflicht zu identischer Domain.`,
    `After a structural check, the whole normalized address is compared: exact or Jaro–Winkler ≥ ${R.email.toFixed(2)}. This has no separate domain threshold or general exact-domain requirement.`,
  ),
  t(
    'Nur Kontakte derselben Firma mit passender vollständiger Namenspaarung sind zulässig. Exakte E-Mail-Treffer haben Vorrang. Mehrere unterschiedliche verbleibende Personen werden nicht allein nach dem höchsten unscharfen Wert ausgewählt.',
    'Only same-company contacts with a matching complete name pair are eligible. Exact email hits take priority. Distinct remaining people are not selected merely by the highest fuzzy score.',
  ),
  t(
    'Nach Erfolg werden die gespeicherten Kontaktdaten übernommen und nicht erneut vorgelesen oder verifiziert. Gespeicherte Adressen oder Namen werden bei der Korrektur nicht verraten.',
    'After success, stored contact data is used without another readback or verification. Stored addresses or names are not disclosed during correction.',
  ),
);
const newEmailSection = section(
  'Zusatzprüfer und begrenzte Wiederholung',
  'Additional verifier and bounded recovery',
  t(
    'Mit aktivierter Funktion arbeitet der KI-Prüfer parallel zum normalen Vergleich. Er prüft die Transkription nur gegen zulässige bekannte Dossier-Kontakte. Er kann eine sprachlich plausible Zuordnung ergänzen, ohne dass sein Treffer selbst die 0.90-Schwelle erfüllen muss.',
    'When enabled, the AI verifier runs alongside ordinary matching. It evaluates the transcript only against eligible known dossier contacts. It can add a plausible speech-based match without that match itself having to meet 0.90.',
  ),
  t(
    'Widersprüchliche Identitäten oder mehrere verbleibende unscharfe Personen werden nicht vom Prüfer beliebig aufgelöst. Ein negatives/unklares KI-Urteil verwirft nicht automatisch einen eindeutigen normalen Treffer.',
    'Conflicting identities or multiple remaining fuzzy people are not arbitrarily resolved by the verifier. A negative/uncertain AI verdict does not automatically veto an unambiguous ordinary match.',
  ),
  t(
    'Nach dem ersten Fehler wird der unklare Teil der eigenen genannten Adresse geklärt. Nach zwei fehlgeschlagenen Prüfungen braucht ein weiterer neuer Versuch eine aktuelle Wahl „erneut versuchen“ statt Mitarbeiter/Ende; der dritte Fehler blockiert. Ein technischer Ausfall des Prüfers allein wird nicht als falsche Kundeneingabe gezählt.',
    'After the first failure, clarify the uncertain part of the caller’s own supplied address. After two failures, another new attempt requires a fresh choice to retry rather than a colleague/end; the third failure blocks verification. A verifier outage alone is not counted as incorrect caller input.',
  ),
);
const ordinaryEmailSection = section(
  'Bestehender CAS: normale E-Mail-Prüfung',
  'Existing CAS: ordinary email verification',
  t(
    'Hier gilt der normale Vergleich ohne den Zusatzprüfer und ohne die spezielle neue-Dossier-Wiederholungswahl. Ein nicht passender Wert bleibt in der Korrektur; der dritte gezählte Fehler blockiert. Eine mehrdeutige Identitätsauswahl blockiert unmittelbar.',
    'This uses ordinary matching without the additional verifier or the special new-dossier retry choice. A mismatch stays in correction; the third counted failure blocks verification. Ambiguous identity selection blocks immediately.',
  ),
);
const issueSection = section(
  'Daten für das neue Ticket',
  'Information for the new ticket',
  t(
    'Erforderlich sind ein aussagekräftiges aktuelles Problem, der aktuelle Maschinenzustand und die Erreichbarkeit. Bereits eindeutig gegebene Angaben werden übernommen; ein Wunschzeitraum ist keine gebuchte Rückrufzeit.',
    'A meaningful current issue, current machine state and availability are required. Clear information already supplied is reused; a preferred window is not a booked callback appointment.',
  ),
  t(
    'Identität, Firma und Maschine bleiben an den vorbereiteten Vorgang gebunden. Eine neue Nebenangabe darf keine abgeschlossene Authentifizierung ersetzen.',
    'Identity, company and machine remain bound to the prepared request. A new incidental detail cannot replace completed authentication.',
  ),
);
const receiptSection = section(
  'Erst der CRM-Beleg bestätigt den Erfolg',
  'Only the CRM receipt confirms success',
  t(
    'Der normale Schreibpfad prüft den abgeschlossenen Kontext und verhindert doppelte Übermittlung derselben laufenden Aktion. Nur eine bestätigte CRM-Antwort erlaubt die Aussage, dass ein Ticket angelegt wurde.',
    'The normal write path checks completed context and prevents resubmitting the same pending operation. Only a confirmed CRM response permits saying a ticket was created.',
  ),
  t(
    'Eine Vorbereitung, lokale Notiz oder unbestätigte Antwort ist kein erfolgreiches Ticket. Bei einem Fehler wird das tatsächliche Ergebnis behandelt und kein Erfolg erfunden.',
    'Preparation, a local note or an unconfirmed response is not a successful ticket. An error is handled according to the actual result, without inventing success.',
  ),
);
const helpSection = section(
  'Gezielte Korrektur oder Mitarbeiter',
  'Targeted correction or colleague',
  t(
    'Ein ausgeschöpfter Prüfrahmen oder eine nicht auflösbare Identitätskollision führt zur Übergabe. Technische Ausfälle werden als technische Probleme behandelt, nicht als falsche Kundendaten oder als Beweis für einen neuen Kontakt.',
    'An exhausted verification budget or unresolved identity collision leads to handoff. Technical outages are treated as technical problems, not incorrect customer data or proof of a new contact.',
  ),
  t(
    'Der Agent kündigt eine beschlossene Übergabe kurz an und ruft das vorhandene Transferwerkzeug im selben Antwortzug auf. Die Übersicht fügt keinen neuen Transferblocker hinzu.',
    'The agent briefly announces a decided transfer and calls the existing transfer tool in the same response turn. This overview adds no new transfer blocker.',
  ),
);

function newCustomerFlow(id: 'a' | 'b' | 'c21'): BusinessFlow {
  const isA = id === 'a';
  const spoken = id === 'c21';
  const label = id === 'a' ? 'A' : id === 'b' ? 'B' : 'C21';
  const entry = spoken
    ? t(
        'Die eingehende Nummer reicht nicht zur Zuordnung; eine angesagte Nummer passt zu einer Kontaktperson der bestätigten Firma.',
        'The incoming number is insufficient for routing; a spoken number matches a contact in the confirmed company.',
      )
    : isA
      ? t(
          'Bekanntes eingehendes Telefon; eine eindeutige Firma mit genau einer verfügbaren Maschine.',
          'Known incoming phone; one unambiguous company with exactly one available machine.',
        )
      : t(
          'Bekanntes eingehendes Telefon; mehrere Maschinen erfordern die Auswahl einer DO.',
          'Known incoming phone; multiple machines require selecting a DO.',
        );
  return {
    id,
    label: t(
      `${label} · ${spoken ? 'Angesagtes Telefon' : isA ? 'Eine Maschine' : 'Mehrere Maschinen'}`,
      `${label} · ${spoken ? 'Spoken phone' : isA ? 'One machine' : 'Several machines'}`,
    ),
    title: t(`${label} — Neues Ticket für bekannten Kontakt`, `${label} — New ticket for a known contact`),
    description: t(
      'Dossier → Firma → Telefonbeleg und vollständige Namen → bei Bedarf E-Mail → neues Ticket.',
      'Dossier → company → phone evidence and complete names → email if required → new ticket.',
    ),
    entry,
    outcome: t(
      'Verifizierter Firmenkontakt; neues Ticket erst nach vollständigen Angaben und CRM-Bestätigung.',
      'Verified company contact; new ticket only after complete information and CRM confirmation.',
    ),
    nodes: [
      node(
        'start',
        'start',
        1,
        0,
        t('Neues Anliegen', 'New request'),
        t('Anliegen und passende Route klären.', 'Establish the request and route.'),
        [section('Einstieg', 'Entry', entry), capabilitySection(id)],
        [sources.provider, sources.reference],
      ),
      node(
        'reference',
        'stage',
        1,
        1,
        t(isA ? 'Maschine bestätigen' : 'Dossier angeben', isA ? 'Confirm machine' : 'Provide dossier'),
        t(
          isA ? 'Die eindeutige Maschine kann vorgeschlagen werden.' : 'DO nennen, korrekt vorlesen und bestätigen.',
          isA ? 'The unambiguous machine may be offered.' : 'Supply, read back and confirm the DO.',
        ),
        [
          referenceSection,
          ...(isA
            ? [
                section(
                  'Angebot, keine automatische Auswahl',
                  'Offer, not automatic selection',
                  t(
                    'Der Vorschlag ist nur bei einer gemeinsamen Firma, einer eindeutigen Maschine und ohne Lookup-Problem zulässig. Ablehnung oder Korrektur führt zur genannten DO.',
                    'The offer requires a common company, one distinct machine and no lookup problem. Rejection or correction leads to the caller-supplied DO.',
                  ),
                ),
              ]
            : []),
        ],
        [sources.prompts, sources.tools],
      ),
      node(
        'company',
        'stage',
        1,
        2,
        t('CRM-Firma bestätigen', 'Confirm CRM company'),
        t('Die Firma der gefundenen Maschine bestätigen.', 'Confirm the company of the found machine.'),
        [companySection],
        [sources.reference, sources.tools],
      ),
      node(
        'phone',
        'stage',
        1,
        3,
        t(
          spoken ? 'Telefon nennen und prüfen' : 'Telefon im Firmenkonto prüfen',
          spoken ? 'Supply and match phone' : 'Match phone in company',
        ),
        t(
          spoken
            ? 'Nummer bestätigen; danach im Firmenkonto abgleichen.'
            : 'Eingehende Nummer muss im Firmenkonto passen.',
          spoken
            ? 'Confirm the number, then match within the company.'
            : 'Incoming number must match within the company.',
        ),
        [
          phoneSection,
          section(
            'Abweichender Einstieg',
            'Different entry result',
            t(
              'Kein Telefon-Treffer im ausgewählten Firmenkonto führt bei einem neuen Ticket zu C22. Ein Lookup-Fehler wird nicht als unbekannter Kunde behandelt. Die Wahl einer Rückrufnummer ist nicht der Return-call-Flow.',
              'No phone match in the selected company leads to C22 for a new ticket. A lookup error is not treated as an unknown customer. Choosing a callback number is not the return-call flow.',
            ),
          ),
        ],
        [sources.reference, sources.tools],
      ),
      node(
        'names',
        'stage',
        1,
        4,
        t('Vorname und Nachname', 'First name and surname'),
        t('Beide vollständigen Felder zusammen prüfen.', 'Check both complete fields together.'),
        [pairSection, matcherSection, retriesSection],
        [sources.reference, sources.matching, sources.attempts],
      ),
      node(
        'identity',
        'decision',
        1,
        5,
        t('Identität ausreichend belegt?', 'Enough identity evidence?'),
        t('Telefon, Person, Namenspaar und Stärke zählen.', 'Phone, person, name pair and strength matter.'),
        [shortcutSection, capabilitySection(id)],
        [sources.reference, sources.people],
      ),
      node(
        'spelling',
        'stage',
        0,
        6,
        t('Nachnamen buchstabieren', 'Spell the surname'),
        t('Eine bedingte zusätzliche Gelegenheit.', 'One conditional additional opportunity.'),
        [weakSection, matcherSection],
        [sources.reference, sources.emailRetry],
      ),
      node(
        'email',
        'stage',
        2,
        7,
        t('E-Mail bei Bedarf prüfen', 'Verify email if needed'),
        t('Akzeptierte Namen brauchen weitere Zuordnung.', 'Accepted names need additional identification.'),
        [basicEmailSection, newEmailSection],
        [sources.reference, sources.email, sources.emailRetry, sources.people],
      ),
      node(
        'verified',
        'stage',
        1,
        8,
        t('Identität bestätigt', 'Identity verified'),
        t('Kontakt und Firma sind verbindlich zugeordnet.', 'Contact and company are bound to this request.'),
        [
          section(
            'Abgeschlossener Nachweis',
            'Completed proof',
            t(
              'Der Server bindet Kontakt, Firma und Dossier an den abgeschlossenen Nachweis. Eine andere Person, Firma oder Referenz ersetzt diesen Nachweis nicht nebenbei.',
              'The server binds contact, company and dossier to completed proof. Another person, company or reference cannot incidentally replace it.',
            ),
          ),
        ],
        [sources.reference],
      ),
      node(
        'help',
        'outcome',
        0,
        8,
        t('Mitarbeiter übernimmt', 'Colleague takes over'),
        t('Prüfung erschöpft oder Zuordnung nicht möglich.', 'Verification exhausted or identity unresolved.'),
        [helpSection],
        [sources.reference, sources.provider],
      ),
      node(
        'details',
        'stage',
        1,
        9,
        t('Neues Problem aufnehmen', 'Collect new issue'),
        t('Problem, Maschinenzustand und Erreichbarkeit.', 'Issue, machine state and availability.'),
        [issueSection],
        [sources.collection, sources.runtime],
      ),
      node(
        'created',
        'outcome',
        1,
        10,
        t('Ticket mit CRM-Beleg', 'Ticket with CRM receipt'),
        t('Erfolg erst nach bestätigter Anlage melden.', 'Announce success only after confirmed creation.'),
        [receiptSection],
        [sources.tools],
      ),
    ],
    edges: [
      edge('start', 'reference'),
      edge('reference', 'company', 'DO gefunden', 'DO found'),
      edge('company', 'phone', 'Firma bestätigt', 'Company confirmed'),
      edge('phone', 'names', 'Kontaktkandidaten', 'Contact candidates'),
      edge('names', 'identity', 'Prüfergebnis', 'Check result'),
      edge('identity', 'verified', 'Ausreichend', 'Sufficient'),
      edge('identity', 'spelling', 'Schwach · Versuch zulässig', 'Weak · retry eligible'),
      edge('identity', 'email', 'Weitere Zuordnung nötig', 'More evidence needed'),
      edge('identity', 'help', 'Limit / Konflikt', 'Limit / conflict'),
      edge('spelling', 'verified', 'Jetzt ausreichend', 'Now sufficient'),
      edge('spelling', 'email', 'Weiter schwach', 'Still weak'),
      edge('spelling', 'help', 'Prüfung erschöpft', 'Checks exhausted'),
      edge('email', 'verified', 'Eine zulässige Identität', 'One eligible identity'),
      edge('email', 'help', 'Nicht auflösbar', 'Cannot resolve'),
      edge('verified', 'details'),
      edge('details', 'created', 'Angaben vollständig', 'Information complete'),
    ],
    notes: [
      commonNote,
      t(
        'A/B beschreiben den historischen Einstieg nach Maschinenanzahl. Der aktive Server unterscheidet anschließend eingehendes, angesagtes oder unbekanntes Telefon im gewählten Firmenkonto.',
        'A/B describe the historical machine-count entry. The active server then distinguishes incoming, spoken or unknown phone evidence in the selected company.',
      ),
      terminalNote,
    ],
  };
}

const c22Flow: BusinessFlow = {
  id: 'c22',
  label: t('C22 · Kontakt aufnehmen', 'C22 · Capture contact'),
  title: t('C22 — Neues Ticket ohne Telefon-Treffer', 'C22 — New ticket without a phone match'),
  description: t(
    'DO und Firma → eigene Kontaktdaten bestätigen → Wiederverwendung oder Einwilligung → neues Ticket.',
    'DO and company → confirm own contact details → reuse or consent → new ticket.',
  ),
  entry: t(
    'Kein Telefon-Treffer innerhalb der Firma des bestätigten Dossiers. Das bedeutet nicht zwingend, dass die Person im CRM fehlt.',
    'No phone match within the company of the confirmed dossier. The person may still exist in CRM.',
  ),
  outcome: t(
    'Nur Vorbereitung eines neuen Tickets; kein authentifizierter Zugriff auf bestehende CAS.',
    'New-ticket preparation only; no authenticated access to existing cases.',
  ),
  nodes: [
    node(
      'start',
      'start',
      1,
      0,
      t('Unbekanntes Telefon', 'Unknown phone'),
      t('Kein passender Kontakt im ausgewählten Konto.', 'No matching phone contact in the selected account.'),
      [capabilitySection('c22'), phoneSection],
      [sources.reference],
    ),
    node(
      'reference',
      'stage',
      1,
      1,
      t('DO und Firma bestätigen', 'Confirm DO and company'),
      t('Maschine, Firma und Rückrufnummer festlegen.', 'Establish machine, company and contact number.'),
      [referenceSection, companySection],
      [sources.reference, sources.tools],
    ),
    node(
      'capture',
      'stage',
      1,
      2,
      t('Drei Felder aufnehmen', 'Capture three fields'),
      t('Vorname → Nachname → vollständige E-Mail.', 'First name → surname → complete email.'),
      [
        section(
          'Eigene Angaben bestätigen',
          'Confirm supplied details',
          t(
            'Jedes vollständige Feld wird vorbereitet, vorgelesen/buchstabiert und mit einer neuen echten Antwort bestätigt. Bei E-Mail bleibt echte Interpunktion erhalten. Es gibt keinen gespeicherten Vergleichswert, der eine Teilbestätigung ersetzen könnte.',
            'Each complete field is prepared, read back/spelled and confirmed with a fresh real answer. Real email punctuation is retained. There is no stored comparison value that can replace full confirmation.',
          ),
          t(
            'Korrekturen widerrufen betroffene Feldnachweise und frühere Einwilligung. Drei ungültige E-Mail-Eingaben blockieren die Aufnahme; reine Buchstabier- und Bestätigungsrunden sind keine Authentifizierungsfehler.',
            'Corrections revoke the affected field proof and earlier consent. Three invalid email submissions block capture; spelling and confirmation turns alone are not authentication failures.',
          ),
        ),
      ],
      [sources.capture],
    ),
    node(
      'classify',
      'decision',
      1,
      3,
      t('Kontakt bereits vorhanden?', 'Does the contact already exist?'),
      t('Bestätigte Angaben nur im aktuellen Konto prüfen.', 'Check confirmed details only in the current account.'),
      [
        section(
          'Wiederverwendung oder Kollision',
          'Reuse or collision',
          t(
            'Bestätigte ganze Namen und E-Mail werden gegen die aktuelle Firma geprüft: exakte passende Identität zuerst, dann begrenzte alternative/fuzzy Wiederverwendung, soweit aktiviert. Eine Kollision mit anderen oder fehlenden E-Mail-Daten kann die Anlage blockieren.',
            'Confirmed whole names and email are checked within the current company: exact compatible identity first, then bounded alternative/fuzzy reuse where enabled. A collision involving different or missing email data can block creation.',
          ),
          t(
            'Ein wiederverwendeter Kontakt wird ausgewählt, nicht mit einer anderen CRM-Zeile verschmolzen. Gespeicherte Namen oder E-Mail werden nicht überschrieben. Wiederverwendung ist kein Authentifizierungsnachweis für alte Tickets.',
            'A reused contact is selected, not merged with another CRM row. Stored names or email are not overwritten. Reuse is not authentication for old tickets.',
          ),
        ),
      ],
      [sources.reuse, sources.tools],
    ),
    node(
      'reuse',
      'stage',
      0,
      4,
      t('Vorhandenen Kontakt verwenden', 'Reuse existing contact'),
      t('Eine kompatible CRM-Identität gefunden.', 'One compatible CRM identity found.'),
      [
        section(
          'Bestehende Zeile',
          'Existing record',
          t(
            'Die bestehende CRM-Zeile wird für das neue Ticket ausgewählt. Dieser Zweig schreibt keinen neuen Kontakt und fragt keine Einwilligung zur Anlage einer neuen Person ab.',
            'The existing CRM record is selected for the new ticket. This branch writes no new contact and does not ask for consent to create a new person.',
          ),
        ),
      ],
      [sources.reuse, sources.reference],
    ),
    node(
      'consent',
      'decision',
      2,
      4,
      t('Neue Person: Einwilligung', 'New person: consent'),
      t('Explizite Zustimmung zu bestätigten Angaben.', 'Explicit consent for confirmed details.'),
      [
        section(
          'Nur wirklich neue Angaben',
          'Only genuinely new details',
          t(
            'Ist die Person tatsächlich neu, muss die Einwilligung zu denselben bestätigten Feldern vorliegen. Fehlende oder abgelehnte Einwilligung erlaubt keine neue Kontaktvorbereitung.',
            'If the person is genuinely new, consent must cover the same confirmed fields. Missing or refused consent does not permit new-contact preparation.',
          ),
          t(
            'create_contact hält die Daten zunächst in der Sitzung. Erst der spätere create_case-Aufruf übergibt sie an die CRM-Brücke; dies ist noch kein bestätigter CRM-Kontakt- oder Ticket-Schreibvorgang.',
            'create_contact initially retains the details in the session. The later create_case call passes them to the CRM bridge; this is not yet a confirmed CRM contact or ticket write.',
          ),
        ),
      ],
      [sources.tools, sources.capture],
    ),
    node(
      'ready',
      'stage',
      1,
      5,
      t('Nur Neuanlage vorbereitet', 'New-ticket preparation ready'),
      t('Wiederverwendete oder neue bestätigte Angaben.', 'Reused or newly confirmed contact details.'),
      [capabilitySection('c22')],
      [sources.reference, sources.capture],
    ),
    node(
      'help',
      'outcome',
      2,
      6,
      t('Mitarbeiter übernimmt', 'Colleague takes over'),
      t('Kollision, fehlende Zustimmung oder Prüfproblem.', 'Collision, no consent or verification problem.'),
      [helpSection],
      [sources.reuse, sources.tools],
    ),
    node(
      'details',
      'stage',
      1,
      6,
      t('Problem und Erreichbarkeit', 'Issue and availability'),
      t('Neue Angaben für dieses Ticket aufnehmen.', 'Collect new information for this ticket.'),
      [issueSection],
      [sources.collection],
    ),
    node(
      'recheck',
      'decision',
      1,
      7,
      t('Vor Schreiben erneut prüfen', 'Recheck before writing'),
      t('Aktuelle Firmenkontakte und gültiger Kontext.', 'Current company contacts and valid context.'),
      [
        section(
          'Frische Kollisionsprüfung',
          'Fresh collision check',
          t(
            'Vor dem Ticket-Schreiben werden die vollständigen Firmenkontakte erneut geladen und klassifiziert. Neue Mehrdeutigkeit oder ein verschwundener wiederverwendeter Kontakt kann blockieren; Einwilligung wird nicht erfunden.',
            'Before the ticket write, all company contacts are reloaded and classified again. New ambiguity or a missing previously reused contact can block creation; consent is not invented.',
          ),
        ),
      ],
      [sources.tools, sources.reuse],
    ),
    node(
      'created',
      'outcome',
      1,
      8,
      t('Neues Ticket mit CRM-Beleg', 'New ticket with CRM receipt'),
      t('C22 bleibt als nicht verifizierter Kontakt gekennzeichnet.', 'C22 remains marked as an unverified contact.'),
      [
        receiptSection,
        section(
          'Begrenzte Berechtigung',
          'Limited permission',
          t(
            'Das neue Ticket nutzt die bestätigte Kontaktaufnahme. Diese Route kann allein keine hohe Priorität begründen und öffnet keine alten CAS.',
            'The new ticket uses confirmed contact capture. This route alone cannot justify high priority and does not open old cases.',
          ),
        ),
      ],
      [sources.tools, sources.reference],
    ),
  ],
  edges: [
    edge('start', 'reference'),
    edge('reference', 'capture', 'Bestätigt', 'Confirmed'),
    edge('capture', 'classify', 'Alle Felder bestätigt', 'All fields confirmed'),
    edge('classify', 'reuse', 'Eine passende Identität', 'One compatible identity'),
    edge('classify', 'consent', 'Wirklich neue Person', 'Genuinely new person'),
    edge('classify', 'help', 'Identitätskollision', 'Identity collision'),
    edge('reuse', 'ready'),
    edge('consent', 'ready', 'Zugestimmt', 'Consent given'),
    edge('consent', 'help', 'Keine Zustimmung', 'No consent'),
    edge('ready', 'details'),
    edge('details', 'recheck'),
    edge('recheck', 'created', 'Weiterhin zulässig', 'Still eligible'),
    edge('recheck', 'help', 'Neue Kollision / ungültig', 'New collision / invalid'),
  ],
  notes: [
    commonNote,
    t(
      '„Wiederverwenden“ ist kein CRM-Merge. Die genaue Kontaktanlage in der externen CRM-Brücke ist hier nicht als eigener bestätigter Schritt dargestellt.',
      '“Reuse” is not a CRM merge. Internal contact creation by the external CRM bridge is not shown as a separately confirmed step here.',
    ),
    terminalNote,
  ],
};

const existingFlow: BusinessFlow = {
  id: 'existing',
  label: t('Bestehendes Ticket', 'Existing ticket'),
  title: t(
    'Bestehendes Ticket — Zugriff nach Identitätsprüfung',
    'Existing ticket — access after identity verification',
  ),
  description: t(
    'CAS bestätigen → im Firmenkonto authentifizieren → Status prüfen → Anliegen bedienen oder Closed → New.',
    'Confirm CAS → authenticate within its company → check status → handle request or Closed → New.',
  ),
  entry: t(
    'Der Anrufer nennt ausdrücklich ein bestehendes Anliegen. Die CAS bestimmt Firma und erlaubten Zugriff.',
    'The caller explicitly refers to an existing case. The CAS determines company and permitted access.',
  ),
  outcome: t(
    'Bearbeitung des geprüften CAS oder gesonderte Vorbereitung eines neuen Tickets bei zulässigem geschlossenem CAS.',
    'Handle the verified CAS, or separately prepare a new ticket for an eligible closed CAS.',
  ),
  nodes: [
    node(
      'start',
      'start',
      1,
      0,
      t('Bestehendes Anliegen', 'Existing-case request'),
      t('Zuerst bestehendes Ticket als Anliegen klären.', 'Establish that this concerns an existing case.'),
      [capabilitySection('existing')],
      [sources.runtime, sources.reference],
    ),
    node(
      'reference',
      'stage',
      1,
      1,
      t('CAS bestätigen und finden', 'Confirm and find CAS'),
      t('Nummer vorlesen, Antwort abwarten, dann suchen.', 'Read back, wait for confirmation, then look up.'),
      [
        section(
          'CAS und Firmengrenze',
          'CAS and company boundary',
          t(
            'Nur ein bestätigter, eindeutig gefundener CAS mit gültigem Firmenkonto wird verwendet. Vor der Identitätsprüfung werden keine gespeicherten Fall- oder Kontaktdetails offengelegt. Nicht gefunden, technische Störung und fehlende Firmenkontakte sind unterschiedliche Ergebnisse.',
            'Only a confirmed, unambiguous CAS with a valid company account is used. Stored case or contact details are not disclosed before identity verification. Not found, technical failure and missing company contacts are distinct outcomes.',
          ),
          t(
            'Ohne bekannte CAS wird geklärt oder ein Mitarbeiter angeboten. Eine unbekannte Nummer erteilt keine Berechtigung für C22-Kontaktaufnahme im bestehenden Fall.',
            'Without a known CAS, clarify or offer a colleague. An unknown number does not authorize C22 contact capture for an existing case.',
          ),
        ),
      ],
      [sources.tools],
    ),
    node(
      'phone',
      'stage',
      1,
      2,
      t('Telefon im CAS-Konto prüfen', 'Match phone in CAS company'),
      t('Eingehende oder angesagte Nummer zuordnen.', 'Match incoming or supplied phone evidence.'),
      [phoneSection, capabilitySection('existing')],
      [sources.reference, sources.tools],
    ),
    node(
      'names',
      'stage',
      1,
      3,
      t('Vollständige Namen prüfen', 'Verify complete names'),
      t('Auch umgekehrte Feldreihenfolge ist möglich.', 'Reversed whole-field order is supported.'),
      [pairSection, matcherSection, retriesSection],
      [sources.reference, sources.matching, sources.attempts],
    ),
    node(
      'identity',
      'decision',
      1,
      4,
      t('Ohne E-Mail ausreichend?', 'Sufficient without email?'),
      t('Eine zulässige Identität und starker Nachname.', 'One eligible identity and a strong surname.'),
      [
        shortcutSection,
        section(
          'Schwacher, aber akzeptierter Nachname',
          'Weak but accepted surname',
          t(
            'Bei bestehendem CAS führt eine akzeptierte schwache Paarung zur E-Mail, ohne die besondere Zusatzchance des neuen Dossiers. Echte Namensfehler erhalten weiterhin die normale Korrekturbehandlung.',
            'On an existing CAS, an accepted weak pair goes to email without the special new-dossier extra opportunity. Genuine name mismatches still receive ordinary correction handling.',
          ),
        ),
      ],
      [sources.reference],
    ),
    node(
      'email',
      'stage',
      2,
      5,
      t('E-Mail zusätzlich prüfen', 'Verify additional email'),
      t('Bei fehlendem Beleg oder mehrdeutiger Zuordnung.', 'When evidence is insufficient or ambiguous.'),
      [basicEmailSection, ordinaryEmailSection],
      [sources.reference, sources.people],
    ),
    node(
      'verified',
      'stage',
      1,
      6,
      t('CAS-Zugriff bestätigt', 'CAS access verified'),
      t('Person, Firma und CAS fest miteinander verbunden.', 'Person, company and CAS are bound together.'),
      [
        section(
          'Gesperrter Bezugsrahmen',
          'Bound reference scope',
          t(
            'Abgeschlossener Kundennachweis gilt genau für den geprüften CAS, dessen Firma und Kontakt. Neue beiläufige Angaben ersetzen den Zugriffskontext nicht.',
            'Completed customer proof applies to the checked CAS, its company and contact. New incidental details cannot replace the access context.',
          ),
        ),
      ],
      [sources.reference],
    ),
    node(
      'help',
      'outcome',
      0,
      6,
      t('Mitarbeiter übernimmt', 'Colleague takes over'),
      t('Prüfung oder eindeutiger Zugriff nicht möglich.', 'Verification or unambiguous access is unavailable.'),
      [helpSection, ordinaryEmailSection],
      [sources.reference, sources.tools],
    ),
    node(
      'status',
      'decision',
      1,
      7,
      t('Welcher Ticketstatus?', 'What is the case status?'),
      t('Erst jetzt verifizierte Falldaten verwenden.', 'Only now use verified case details.'),
      [
        section(
          'Geschlossen ist ein definierter Zustand',
          'Closed is a defined state',
          t(
            'Nur erkannte geschlossene Statuswerte öffnen den gesonderten Nachfolgepfad. Unbekannter Status, ExpertFinished und ClosureRequested werden nicht als geschlossen interpretiert.',
            'Only recognized closed status values open the separate followup route. Unknown status, ExpertFinished and ClosureRequested are not interpreted as closed.',
          ),
        ),
      ],
      [sources.provider, sources.closed],
    ),
    node(
      'open',
      'stage',
      0,
      8,
      t('Bestehendes Anliegen bedienen', 'Handle existing request'),
      t('Zulässige Auskunft, Ergänzung oder Eskalation.', 'Permitted status, information or escalation.'),
      [
        section(
          'Erlaubte Aktionen',
          'Permitted actions',
          t(
            'Der aktuelle CAS und der aktuelle Wunsch bestimmen die zulässige Aktion: Status, Zusatzinformation, Eskalation oder Schließungsanfrage. Eine lokale Schließungsanfrage ist nicht automatisch ein im CRM geschlossenes Ticket.',
            'The current CAS and request determine the permitted action: status, additional information, escalation or a closure request. A local closure request is not automatically a closed CRM ticket.',
          ),
        ),
      ],
      [sources.tools, sources.runtime],
    ),
    node(
      'closed',
      'outcome',
      2,
      8,
      t('Closed → New oder Mitarbeiter', 'Closed → New or colleague'),
      t('Neues Ticket nur nach gesonderter Bestätigung.', 'New ticket only after separate confirmation.'),
      [
        section(
          'Nächste Ansicht',
          'Next view',
          t(
            'Bei geeignetem geschlossenem CAS: neues Ticket für dieselbe Maschine/Firma anbieten oder einen Mitarbeiter. Die Einzelheiten stehen im Pfad „Closed → New“. Das alte Ticket wird nicht geändert.',
            'For an eligible closed CAS: offer a new ticket for the same machine/company or a colleague. See the “Closed → New” path for the details. The old case is not changed.',
          ),
        ),
      ],
      [sources.closed],
    ),
    node(
      'done',
      'outcome',
      0,
      9,
      t('Tatsächliches Ergebnis mitteilen', 'Report the actual outcome'),
      t('Änderungen nur mit bestätigtem CRM-Ergebnis.', 'Confirm changes only from the actual CRM result.'),
      [receiptSection],
      [sources.tools],
    ),
  ],
  edges: [
    edge('start', 'reference'),
    edge('reference', 'phone', 'CAS und Konto gefunden', 'CAS and account found'),
    edge('phone', 'names'),
    edge('names', 'identity', 'Prüfergebnis', 'Check result'),
    edge('identity', 'verified', 'Ausreichend', 'Sufficient'),
    edge('identity', 'email', 'Zusatzbeleg nötig', 'Extra evidence needed'),
    edge('identity', 'help', 'Prüfgrenze erreicht', 'Check limit reached'),
    edge('email', 'verified', 'Eindeutig bestätigt', 'Uniquely verified'),
    edge('email', 'help', 'Konflikt / Limit', 'Conflict / limit'),
    edge('verified', 'status'),
    edge('status', 'open', 'Nicht als geschlossen erkannt', 'Not classified closed'),
    edge('status', 'closed', 'Geschlossen und geeignet', 'Closed and eligible'),
    edge('open', 'done'),
  ],
  notes: [
    commonNote,
    t(
      'Der angesagte Telefonweg ohne E-Mail gilt für normales Navigate-existing. Er erweitert weder Callback noch die KI-E-Mail-Prüfung.',
      'The spoken-phone email-free route applies to ordinary Navigate existing-ticket. It does not broaden callback or AI email verification.',
    ),
    terminalNote,
  ],
};

const closedFlow: BusinessFlow = {
  id: 'closed-new',
  label: t('Geschlossen → neues Ticket', 'Closed → new ticket'),
  title: t('Geschlossener CAS → neues Ticket', 'Closed CAS → new ticket'),
  description: t(
    'Verifizierter geschlossener CAS → ausdrücklicher Wunsch → Maschine/Firma neu bestätigen → neues Problem → neues Ticket.',
    'Verified closed CAS → explicit request → fresh machine/company confirmation → new issue → new ticket.',
  ),
  entry: t(
    'Normaler Navigate-existing-Anruf mit abgeschlossenem Kundennachweis und erkanntem geschlossenem CAS; kein Callback oder Technikerfall.',
    'Ordinary Navigate existing-ticket call with completed customer proof and a recognized closed CAS; not callback or technician.',
  ),
  outcome: t(
    'Ein neues Ticket für dieselbe Maschine, Firma und geprüfte Person. Das alte geschlossene Ticket bleibt erhalten.',
    'One new ticket for the same machine, company and verified person. The old closed ticket is retained.',
  ),
  nodes: [
    node(
      'start',
      'start',
      1,
      0,
      t('Geschlossener CAS verifiziert', 'Closed CAS verified'),
      t('Identität und definierter Status sind bestätigt.', 'Identity and defined closed status are confirmed.'),
      [
        capabilitySection('closed-new'),
        section(
          'Zulässigkeit',
          'Eligibility',
          t(
            'Erforderlich: Navigate, aktive Reference-first-/Closed-choice-Funktionen, unveränderter abgeschlossener Kundennachweis, normaler existing-ticket-Modus und keine Sperre, Beendigung oder bereits laufende/abgeschlossene Neuanlage.',
            'Requires Navigate, enabled reference-first/closed-choice features, unchanged completed customer proof, ordinary existing-ticket mode and no lock, termination or already pending/completed creation.',
          ),
          t(
            'Erkannte normalisierte Statuswerte: problemsolved, informationprovided, canceled, merged, cancelled, closed, resolved. ExpertFinished, ClosureRequested und unbekannte Werte sind ausgeschlossen.',
            'Recognized normalized status keys: problemsolved, informationprovided, canceled, merged, cancelled, closed, resolved. ExpertFinished, ClosureRequested and unknown values are excluded.',
          ),
        ),
      ],
      [sources.closed, sources.provider],
    ),
    node(
      'choice',
      'decision',
      1,
      1,
      t('Was möchte der Anrufer?', 'What does the caller want?'),
      t('Neues Ticket oder Unterstützung durch Mitarbeiter.', 'A new ticket or assistance from a colleague.'),
      [
        section(
          'Aktueller ausdrücklicher Wunsch',
          'Current explicit request',
          t(
            'Ein klarer Wunsch nach einem neuen Ticket startet direkt die Vorbereitung. Andernfalls wird neues Ticket für dieselbe Maschine/Firma oder Mitarbeiter angeboten. Ein früheres Ja oder eine bloße Transferablehnung ist kein neuer Ticketauftrag.',
            'A clear new-ticket request starts preparation directly. Otherwise offer a new ticket for the same machine/company or a colleague. An earlier yes or merely refusing transfer is not a new-ticket request.',
          ),
        ),
      ],
      [sources.closed, sources.runtime],
    ),
    node(
      'prepare',
      'stage',
      1,
      2,
      t('Gleiche Maschine neu lesen', 'Reload the same machine'),
      t('DO, Maschine und CRM-Firma erneut prüfen.', 'Recheck DO, machine and CRM company.'),
      [
        section(
          'Bestehenden Bezug erhalten',
          'Preserve the existing scope',
          t(
            'Maschine und Firma werden erneut gelesen und müssen zum alten verifizierten CAS-Konto gehören. Eine abweichende Maschine wird nicht stillschweigend übernommen. Fehlt die alte Maschinenreferenz, darf nur die vom Anrufer genannte DO für diese Maschine geprüft werden.',
            'Machine and company are reread and must belong to the old verified CAS account. A different machine is not silently accepted. If the old machine reference is missing, only the caller-supplied DO for that machine may be checked.',
          ),
          t(
            'Fehler oder veraltete Antworten verändern den alten CAS-Nachweis nicht. Ein neuer Kontakt wird nicht ausgewählt. Bei nicht verfügbarer Prüfung: erneut versuchen oder Mitarbeiter anbieten und die Wahl abwarten.',
            'Errors or stale replies do not change the old CAS proof. No new contact is selected. If the check is unavailable, offer retry or a colleague and wait for that choice.',
          ),
        ),
      ],
      [sources.closed, sources.tools],
    ),
    node(
      'confirm',
      'decision',
      1,
      3,
      t('Maschine und Firma bestätigen', 'Confirm machine and company'),
      t('Eine neue gemeinsame Frage und echte neue Antwort.', 'One fresh combined question and real new answer.'),
      [
        section(
          'Frische, passende Bestätigung',
          'Fresh matching confirmation',
          t(
            'Der Agent nennt DO, Maschinenname und exakte CRM-Firma gemeinsam. Eine neue Antwort muss diesen Vorschlag bestätigen. Die frühere CAS-, Namen- oder Firmenantwort wird nicht wiederverwendet.',
            'The agent names DO, machine name and exact CRM company together. A fresh answer must confirm this proposal. Earlier CAS, name or company answers are not reused.',
          ),
          t(
            'Der Agent deutet die gesamte Antwort. Der Server prüft aktuelle Frage/Antwort, Vorschlagsrevision und passende Maschinen-/Firmendaten. Die Prüfung behauptet keine unabhängige vollständige Spracherkennung durch den Server.',
            'The agent interprets the whole reply. The server checks current question/answer evidence, proposal revision and matching machine/company data. This does not claim independent full language understanding by the server.',
          ),
        ),
      ],
      [sources.closed],
    ),
    node(
      'cancel',
      'outcome',
      0,
      4,
      t('Alter CAS bleibt gültig', 'Original CAS remains valid'),
      t('Vorschlag abgelehnt oder abgebrochen.', 'Proposal rejected or cancelled.'),
      [
        section(
          'Keine unbemerkte Neuanlage',
          'No silent creation',
          t(
            'Ablehnung oder Abbruch übernimmt keinen neuen Vorgang. Der ursprüngliche verifizierte CAS bleibt unverändert; Unklarheit braucht eine frische Klärung.',
            'Rejection or cancellation does not publish a new request. The original verified CAS remains unchanged; uncertainty needs fresh clarification.',
          ),
        ),
      ],
      [sources.closed],
    ),
    node(
      'inherit',
      'stage',
      1,
      4,
      t('Geprüfte Identität übernehmen', 'Inherit verified identity'),
      t('Gleicher Kontakt, gleiche Firma und Maschine.', 'Same contact, company and machine.'),
      [
        section(
          'Vorbereitung, noch kein Ticket',
          'Preparation, not a created ticket',
          t(
            'Die Bestätigung wechselt in einen neuen Dossier-Kontext mit demselben geprüften Kontakt. Namen oder E-Mail werden nicht erneut abgefragt. Diese Bestätigung schreibt noch kein CRM-Ticket und ändert den alten CAS nicht.',
            'Confirmation enters a new dossier context with the same verified contact. Names or email are not requested again. This confirmation does not yet write a CRM ticket or change the old CAS.',
          ),
        ),
      ],
      [sources.closed, sources.reference],
    ),
    node(
      'details',
      'stage',
      1,
      5,
      t('Neues Problem aufnehmen', 'Collect the new issue'),
      t('Alte Problembeschreibung nicht wiederverwenden.', 'Do not reuse the old issue description.'),
      [
        issueSection,
        section(
          'Grenze zum alten Verlauf',
          'Boundary with old history',
          t(
            'Ein bereits im neuen Wunsch genanntes Problem bleibt erhalten. Frühere CAS-Beschreibung, Maschinenzustand und Erreichbarkeit werden nicht als neue Angaben kopiert.',
            'An issue already stated in the new request is retained. Earlier CAS issue, machine state and availability are not copied as new information.',
          ),
        ),
      ],
      [sources.collection],
    ),
    node(
      'write',
      'stage',
      1,
      6,
      t('Neues Ticket sicher anlegen', 'Create the new ticket'),
      t('Kontext nochmals prüfen; laufende Aktion nicht doppeln.', 'Recheck context; do not duplicate pending work.'),
      [receiptSection],
      [sources.tools, sources.closed],
    ),
    node(
      'created',
      'outcome',
      1,
      7,
      t('Neue CAS bestätigt', 'New CAS confirmed'),
      t('Nur die neue CRM-Bestätigung als Erfolg verwenden.', 'Use only the new CRM receipt as success.'),
      [
        section(
          'Zwei getrennte Vorgänge',
          'Two separate cases',
          t(
            'Der neue CRM-Beleg gehört zum neuen Ticket. Spätere Ergänzungen dürfen nicht versehentlich am alten geschlossenen CAS landen.',
            'The new CRM receipt belongs to the new ticket. Later information must not accidentally be appended to the old closed CAS.',
          ),
        ),
      ],
      [sources.tools, sources.prompts],
    ),
    node(
      'help',
      'outcome',
      2,
      2,
      t('Mitarbeiter übernimmt', 'Colleague takes over'),
      t('Übergabe gewünscht oder angeboten und angenommen.', 'Transfer requested, or offered and accepted.'),
      [helpSection],
      [sources.provider, sources.closed],
    ),
  ],
  edges: [
    edge('start', 'choice'),
    edge('choice', 'prepare', 'Neues Ticket gewünscht', 'New ticket requested'),
    edge('choice', 'help', 'Mitarbeiter gewünscht', 'Colleague requested'),
    edge('prepare', 'confirm', 'Gleicher Bezug geprüft', 'Same scope checked'),
    edge('prepare', 'help', 'Mitarbeiter gewählt', 'Colleague chosen'),
    edge('confirm', 'inherit', 'Frisch bestätigt', 'Freshly confirmed'),
    edge('confirm', 'cancel', 'Abgelehnt / abgebrochen', 'Rejected / cancelled'),
    edge('inherit', 'details'),
    edge('details', 'write', 'Neue Angaben vollständig', 'New information complete'),
    edge('write', 'created', 'CRM-Erfolg', 'CRM success'),
  ],
  notes: [
    commonNote,
    t(
      'Kein allgemeiner Wechsel aus beliebigen bestehenden Tickets: der neue Pfad ist an den verifizierten geschlossenen CAS gebunden.',
      'This is not a general switch from arbitrary existing tickets: the new route is bound to the verified closed CAS.',
    ),
    terminalNote,
  ],
};

const callbackFlow: BusinessFlow = {
  id: 'callback',
  label: t('Rückruf / Return-call', 'Callback / return-call'),
  title: t('Rückruf zu einem bestehenden Fall', 'Return call about an existing case'),
  description: t(
    'CAS klären → Firmenkontakt authentifizieren → Erreichbarkeit ergänzen → Ergebnis beachten und übergeben.',
    'Establish CAS → authenticate company contact → record availability → follow the result and hand off.',
  ),
  entry: t(
    'Ein Rückruf zu einem bestehenden Fall. Dies ist nicht die Wahl einer Rückrufnummer bei einem neuen Ticket.',
    'A return call about an existing case. This is not choosing a callback phone for a new ticket.',
  ),
  outcome: t(
    'Bestätigte Rückrufinformation beim bestehenden Fall und Übergabe nach dem tatsächlichen Ergebnis.',
    'Confirmed callback information on the existing case and handoff according to the actual result.',
  ),
  nodes: [
    node(
      'start',
      'start',
      1,
      0,
      t('Rückruf-Anliegen', 'Return-call request'),
      t('Bestehender Fall mit Rückrufmodus.', 'Existing case with return-call mode.'),
      [capabilitySection('callback')],
      [sources.tools, sources.runtime],
    ),
    node(
      'reference',
      'decision',
      1,
      1,
      t('CAS bekannt und gefunden?', 'CAS known and found?'),
      t('Bestätigte Fallnummer als Bezugsrahmen.', 'A confirmed case number defines the scope.'),
      [
        section(
          'Ohne CAS nicht raten',
          'Do not guess without a CAS',
          t(
            'Wer wegen eines verpassten Anrufs ohne CAS zurückruft, bekommt einen Mitarbeiter angeboten. Erst die Annahme dieses Angebots entscheidet über die Übergabe; der verpasste Anruf allein ist keine Zustimmung.',
            'A caller returning a missed call without a CAS is offered a colleague. Acceptance of the offer determines transfer; the missed call alone is not consent.',
          ),
        ),
      ],
      [sources.runtime, sources.provider],
    ),
    node(
      'names',
      'stage',
      1,
      2,
      t('Telefon und Namen prüfen', 'Verify phone and names'),
      t('Nur Kontakte aus der Firma des CAS.', 'Only contacts in the CAS company.'),
      [phoneSection, pairSection, matcherSection, retriesSection],
      [sources.reference, sources.matching],
    ),
    node(
      'identity',
      'decision',
      1,
      3,
      t('Eingehender Beleg ausreichend?', 'Enough incoming-phone evidence?'),
      t('Angesagtes Telefon erhält hier keine neue Abkürzung.', 'Spoken phone has no new shortcut here.'),
      [shortcutSection, capabilitySection('callback')],
      [sources.reference],
    ),
    node(
      'email',
      'stage',
      2,
      4,
      t('E-Mail falls erforderlich', 'Email when required'),
      t('Normale CAS-Prüfung, ohne Zusatzprüfer.', 'Ordinary CAS matching, without the AI verifier.'),
      [basicEmailSection, ordinaryEmailSection],
      [sources.reference, sources.people],
    ),
    node(
      'verified',
      'stage',
      1,
      5,
      t('Fallzugriff verifiziert', 'Case access verified'),
      t('Erst jetzt am bestehenden Fall weiterarbeiten.', 'Only now continue on the existing case.'),
      [
        section(
          'Status und begrenzter Zugriff',
          'Status and limited access',
          t(
            'Rückrufmodus bleibt an denselben geprüften CAS gebunden. Bei geschlossenem CAS gilt die bestehende Closed-Case-Behandlung; der neue Closed → New-Pfad und seine Ausnahme für fehlende Maschine sind ausgeschlossen.',
            'Return-call mode stays bound to the same verified CAS. A closed CAS follows existing closed-case handling; the new Closed → New route and its missing-machine exception are excluded.',
          ),
        ),
      ],
      [sources.reference, sources.closed, sources.tools],
    ),
    node(
      'availability',
      'stage',
      1,
      6,
      t('Erreichbarkeit erfassen', 'Capture availability'),
      t('Wunschzeitraum als Anfrage aufnehmen.', 'Record a preferred window as a request.'),
      [
        section(
          'Kein Terminzusage',
          'No appointment promise',
          t(
            'Bei einem offenen verifizierten Fall wird die gewünschte Erreichbarkeit aufgenommen. Eine andere Rückrufnummer wird als Zusatzinformation erfasst; die gespeicherte CRM-Telefonnummer wird nicht dadurch überschrieben.',
            'For an open verified case, capture requested availability. A different callback number is recorded as additional information; this does not overwrite the stored CRM phone.',
          ),
          t(
            'Die Zeit ist eine Präferenz, kein vereinbarter Termin und keine garantierte Rückrufzeit.',
            'The window is a preference, not a booked appointment or guaranteed callback time.',
          ),
        ),
      ],
      [sources.runtime, sources.tools],
    ),
    node(
      'save',
      'stage',
      1,
      7,
      t('Information einmal speichern', 'Save information once'),
      t('Laufenden Schreibvorgang nicht wiederholen.', 'Do not duplicate a pending write.'),
      [
        section(
          'Rückrufbeleg',
          'Callback receipt',
          t(
            'Gleiche ausstehende oder abgeschlossene Angaben werden nicht doppelt geschrieben. Nur ein bestätigter CRM-Update-Beleg gilt als gespeichert; ein unbestätigtes Ergebnis muss so behandelt werden.',
            'Identical pending or completed information is not written twice. Only a confirmed CRM update receipt counts as saved; an unconfirmed result must be handled accordingly.',
          ),
        ),
      ],
      [sources.tools],
    ),
    node(
      'done',
      'outcome',
      1,
      8,
      t('Ergebnis und Übergabe', 'Outcome and handoff'),
      t('Tatsächliches Speicherergebnis berücksichtigen.', 'Follow the actual save result.'),
      [
        helpSection,
        section(
          'Kein erfundener Erfolg',
          'No invented success',
          t(
            'Nach dem Callback-Schritt folgt der Agent dem tatsächlichen Tool-Ergebnis und übergibt an einen Mitarbeiter. Ein fehlgeschlagenes Update wird nicht als gespeichert bezeichnet.',
            'After the callback step, the agent follows the actual tool result and hands off to a colleague. A failed update is not reported as saved.',
          ),
        ),
      ],
      [sources.runtime, sources.tools],
    ),
    node(
      'help',
      'outcome',
      0,
      5,
      t('Klärung mit Mitarbeiter', 'Clarify with a colleague'),
      t('Kein CAS, gesperrte Prüfung oder geschlossener Fall.', 'No CAS, blocked verification or closed case.'),
      [helpSection],
      [sources.provider, sources.runtime],
    ),
  ],
  edges: [
    edge('start', 'reference'),
    edge('reference', 'names', 'CAS bestätigt', 'CAS confirmed'),
    edge('reference', 'help', 'Ohne CAS · Angebot angenommen', 'No CAS · offer accepted'),
    edge('names', 'identity'),
    edge('identity', 'verified', 'Incoming + starke Namen', 'Incoming + strong names'),
    edge('identity', 'email', 'Weitere Prüfung nötig', 'Further check needed'),
    edge('identity', 'help', 'Prüfung erschöpft', 'Checks exhausted'),
    edge('email', 'verified', 'Eindeutig bestätigt', 'Uniquely verified'),
    edge('email', 'help', 'Konflikt / Limit', 'Conflict / limit'),
    edge('verified', 'availability', 'Offener Fall', 'Open case'),
    edge('verified', 'help', 'Geschlossener Fall', 'Closed case'),
    edge('availability', 'save'),
    edge('save', 'done'),
  ],
  notes: [
    commonNote,
    t(
      'Bekanntes eingehendes Telefon kann E-Mail weiterhin ersetzen. Die neue Abkürzung für ein angesagtes Telefon in normalem existing-ticket gilt nicht im Rückrufmodus.',
      'A known incoming phone can still replace email. The new spoken-phone shortcut for ordinary existing-ticket does not apply in return-call mode.',
    ),
    terminalNote,
  ],
};

const technicianFlow: BusinessFlow = {
  id: 'technician',
  label: t('Registrierter Servicetechniker', 'Registered service technician'),
  title: t('Servicetechniker handelt für einen Kunden', 'Service technician acting for a customer'),
  description: t(
    'Registrierung erkennen → Rolle klären → Kundenmaschine/Firma → Kundenkontakt auswählen → delegiertes neues Ticket.',
    'Recognize registration → establish role → customer machine/company → select customer contact → delegated new ticket.',
  ),
  entry: t(
    'Registrierter ZwickRoell-Servicetechniker, über die vertrauenswürdige eingehende Nummer erkannt, der für einen Kunden handelt.',
    'Registered ZwickRoell service technician recognized through the trusted incoming phone and acting for a customer.',
  ),
  outcome: t(
    'Neues delegiertes Ticket mit ausgewähltem Kundenkontakt und Technikerzuordnung; kein Zugriff auf alte CAS aus dieser Berechtigung.',
    'New delegated ticket with the selected customer contact and engineer attribution; this permission grants no access to old cases.',
  ),
  nodes: [
    node(
      'start',
      'start',
      1,
      0,
      t('Registrierung erkannt', 'Registration recognized'),
      t('Vertrauenswürdige Nummer und gültiger Technikerdatensatz.', 'Trusted number and valid engineer record.'),
      [
        section(
          'Eigene Technikerberechtigung',
          'Engineer’s own permission',
          t(
            'Die aktive Technikersuche muss einen gültigen registrierten Datensatz zur eingehenden Nummer liefern. Die getrennte Berechtigung bleibt an genau diesen Datensatz und diese Nummer gebunden. Der Titel „Ingenieur“ allein reicht nicht.',
            'Enabled engineer lookup must return a valid registered record for the incoming phone. Separate permission remains bound to that exact record and phone. The title “engineer” alone is insufficient.',
          ),
          t(
            'Auf dieser erkannten Route werden nicht nochmals eigener Vorname, Nachname oder E-Mail des Technikers abgefragt. Diese Berechtigung ist keine normale Kundenauthentifizierung.',
            'On this recognized route, the engineer’s own first name, surname or email is not challenged again. This permission is not ordinary customer authentication.',
          ),
        ),
        capabilitySection('technician'),
      ],
      [sources.engineer, sources.provider],
    ),
    node(
      'role',
      'decision',
      1,
      1,
      t('Für einen Kunden tätig?', 'Acting for a customer?'),
      t('Erkennung nennen; dann Rollenentscheidung abwarten.', 'Announce recognition, then wait for the role answer.'),
      [
        section(
          'Rollenwahl',
          'Role choice',
          t(
            'Beim automatisch erkannten Techniker bleibt jede Ticketwahl bis zur Erkennungsansage gesperrt, auch der Wechsel in die Kundenrolle. Normalerweise folgt die Frage nach Ticket für einen Kunden oder Mitarbeiter. Ein bereits ausdrücklich genanntes eigenes Kundenanliegen darf nach der Ansage gezielt bestätigt werden; diese Rolle wird nicht ungefragt angeboten.',
            'For an automatically recognized engineer, every ticket choice remains blocked until recognition is announced, including switching to customer mode. Normally the question offers a customer ticket or a colleague. An already explicitly stated personal customer request may be confirmed after recognition; that role is not offered unprompted.',
          ),
          t(
            'Der Agent soll die neue Antwort im Zusammenhang mit der tatsächlichen Frage auswerten. Bestätigt sie ausdrücklich die eigene Kundenrolle, folgt die normale Kundenprüfung. Ein allgemeiner Ticketwunsch ist dafür nicht ausreichend. Nach einem gültigen Ausstieg wird die Technikerroute im selben Anruf nicht erneut betreten.',
            'The agent must interpret the fresh answer in relation to the actual question. If it explicitly confirms the personal customer role, ordinary customer verification follows. A generic ticket request is insufficient. After a valid exit, the engineer route is not reentered in the same call.',
          ),
        ),
      ],
      [sources.engineer, sources.tools],
    ),
    node(
      'customer-mode',
      'outcome',
      0,
      2,
      t('Normaler Kundenpfad', 'Ordinary customer path'),
      t('Kundenprüfung gemäß tatsächlichem Anliegen.', 'Customer verification for the actual request.'),
      [
        section(
          'Keine übertragene Berechtigung',
          'No transferred permission',
          t(
            'Die Technikererkennung ersetzt keinen Nachweis für einen persönlichen Kundenfall oder bestehenden CAS. Dafür gelten die gewöhnlichen Kundenregeln.',
            'Engineer recognition does not replace proof for a personal customer case or existing CAS. Ordinary customer rules apply.',
          ),
        ),
      ],
      [sources.engineer],
    ),
    node(
      'reference',
      'stage',
      1,
      2,
      t('Kundenmaschine und Firma', 'Customer machine and company'),
      t('DO suchen und die gefundene Firma bestätigen.', 'Look up the DO and confirm its company.'),
      [
        companySection,
        section(
          'Kundenbezug',
          'Customer scope',
          t(
            'Kontakte werden nur aus der Firma der ausgewählten Kundenmaschine geladen. Der Techniker selbst darf nicht als Kundenkontakt gewählt werden.',
            'Contacts are loaded only from the selected customer machine’s company. The engineer must not be selected as the customer contact.',
          ),
        ),
      ],
      [sources.engineer],
    ),
    node(
      'names',
      'stage',
      1,
      3,
      t('Kundenkontakt benennen', 'Name the customer contact'),
      t('Vollständiger Vor- und Nachname des Kunden.', 'The customer’s complete first name and surname.'),
      [
        matcherSection,
        section(
          'Auswahl statt Anruferauthentifizierung',
          'Selection, not caller authentication',
          t(
            'Vorname und Nachname werden innerhalb dieses Kundenkontos geprüft. Exakte vollständige Namen haben Vorrang; geeignete Zeilen werden nach der vorhandenen Mailbox-Gruppierung zu Personen zusammengefasst. Dies setzt nicht die normale Kundenberechtigung.',
            'First name and surname are checked within this customer account. Exact complete names take priority; eligible rows are grouped into people using the existing mailbox grouping. This does not grant ordinary customer authorization.',
          ),
          t(
            'Anders als bei normalen Kunden müssen für den Weg ohne Kunden-E-Mail sowohl Vorname als auch Nachname ausreichend stark sein. Ein Kunden-Telefontreffer ist dafür nicht erforderlich.',
            'Unlike ordinary customers, both first name and surname must be sufficiently strong to omit customer email. No customer-phone match is required for that selection.',
          ),
        ),
      ],
      [sources.engineer, sources.matching],
    ),
    node(
      'selection',
      'decision',
      1,
      4,
      t('Eine starke Kontaktperson?', 'One strong contact person?'),
      t('Beide Namen stark und eine zulässige Person.', 'Both names strong and one eligible person.'),
      [
        section(
          'Unterschied zur normalen Route',
          'Difference from ordinary verification',
          t(
            'Schwach bedeutet hier für eines der beiden Namen: phoneticBoost und Ergebniswert ≤ 0.85. Eine nur phonetische oder mehrdeutige Auswahl benötigt Kunden-E-Mail. Ein gespeicherter Datensatz ohne brauchbare E-Mail kann dann nicht stillschweigend ausgewählt werden.',
            'Weak here means phoneticBoost and score ≤ 0.85 for either name. A merely phonetic or ambiguous selection requires customer email. A stored record without usable email cannot then be silently selected.',
          ),
        ),
      ],
      [sources.engineer],
    ),
    node(
      'email',
      'stage',
      2,
      5,
      t('Kunden-E-Mail auswählen', 'Match customer email'),
      t('Kein KI-Prüfer; eigener Mehrdeutigkeitsabstand.', 'No AI verifier; separate ambiguity margin.'),
      [
        section(
          'Techniker-spezifische Auswahl',
          'Engineer-specific selection',
          t(
            'Eine passende Mailbox nach dem vorhandenen Mailbox-Schlüssel hat Vorrang. Sonst gelten Strukturprüfung und Ganzadressvergleich ≥ 0.90. Bei zwei passenden unterschiedlichen Kandidaten muss der beste Wert mindestens 0.08 über dem nächsten liegen; sonst übernimmt ein Mitarbeiter.',
            'A matching mailbox under the existing mailbox key takes priority. Otherwise structural validity and whole-address similarity ≥ 0.90 apply. With two matching distinct candidates, the best score must exceed the next by at least 0.08; otherwise a colleague takes over.',
          ),
          t(
            'Der Mailbox-Schlüssel ignoriert Punkte, Bindestriche und Unterstriche in der gesamten Adresse; er ist nicht strikte E-Mail-Gleichheit. Dieser 0.08-Abstand gilt nicht für die gewöhnliche Kunden-E-Mail-Prüfung.',
            'The mailbox key ignores dots, hyphens and underscores throughout the address; it is not strict email equality. This 0.08 margin does not apply to ordinary customer email verification.',
          ),
        ),
      ],
      [sources.engineer, sources.matching],
    ),
    node(
      'ready',
      'stage',
      1,
      6,
      t('Kundenkontakt ausgewählt', 'Customer contact selected'),
      t('Technikerberechtigung und Kontakt getrennt gebunden.', 'Engineer permission and contact separately bound.'),
      [
        section(
          'Delegierter Vorgang',
          'Delegated request',
          t(
            'Die Kundenkontaktperson wird dem neuen Ticket zugeordnet, der registrierte Techniker als ServiceEngineer erfasst. Es entsteht kein neuer Kundenkontakt und keine normale Berechtigung für alte Kundentickets.',
            'The selected customer contact is assigned to the new ticket and the registered engineer recorded as ServiceEngineer. No new customer contact or ordinary access to old customer cases is created.',
          ),
        ),
      ],
      [sources.engineer, sources.tools],
    ),
    node(
      'help',
      'outcome',
      0,
      6,
      t('Mitarbeiter übernimmt', 'Colleague takes over'),
      t('Keine eindeutige Auswahl oder Prüfung gesperrt.', 'No unambiguous selection or verification blocked.'),
      [
        helpSection,
        section(
          'Eigener Wiederholungsrahmen',
          'Separate retry policy',
          t(
            'Der dritte Fehler je Kontaktfeld blockiert. Eine tatsächlich andere, zu allen bisherigen Werten unähnliche Angabe kann den Feldzähler zurücksetzen und die Kontaktprüfung fortsetzen. Wiederholte oder ähnlich wechselnde Werte schaffen keinen neuen Rahmen; erneute DO-Suche löscht die Versuche nicht.',
            'The third failure per contact field blocks. A genuinely different value dissimilar to all previous values can reset that field’s counter and resume contact selection. Repeated or similarly alternating values do not create a new budget; reloading the DO does not clear attempts.',
          ),
        ),
      ],
      [sources.engineer],
    ),
    node(
      'details',
      'stage',
      1,
      7,
      t('Neues Kundenproblem', 'New customer issue'),
      t('Problem, Maschinenzustand und Erreichbarkeit.', 'Issue, machine state and availability.'),
      [issueSection],
      [sources.collection, sources.engineer],
    ),
    node(
      'created',
      'outcome',
      1,
      8,
      t('Delegiertes Ticket bestätigt', 'Delegated ticket confirmed'),
      t('Erfolg nur mit tatsächlichem CRM-Beleg.', 'Success only with an actual CRM receipt.'),
      [receiptSection],
      [sources.tools],
    ),
  ],
  edges: [
    edge('start', 'role'),
    edge('role', 'reference', 'Für Kunden', 'For a customer'),
    edge('role', 'customer-mode', 'Ausdrücklich als Kunde', 'Explicitly as customer'),
    edge('reference', 'names', 'Firma bestätigt', 'Company confirmed'),
    edge('names', 'selection', 'Namensprüfung', 'Name check'),
    edge('selection', 'ready', 'Eindeutig und stark', 'Unambiguous and strong'),
    edge('selection', 'email', 'Schwach / mehrdeutig', 'Weak / ambiguous'),
    edge('selection', 'help', 'Keine Auswahl / Limit', 'No selection / limit'),
    edge('email', 'ready', 'Eindeutige Auswahl', 'Unambiguous selection'),
    edge('email', 'help', 'Ungeklärt / Limit', 'Unresolved / limit'),
    edge('ready', 'details'),
    edge('details', 'created', 'Vollständig und gespeichert', 'Complete and saved'),
  ],
  notes: [
    commonNote,
    t(
      'Die Technikerroute erteilt eine delegierte Neuanlageberechtigung. Ihre Kontaktwahl, E-Mail-Auswahl und Wiederholungsregeln sind eigenständig.',
      'The engineer route grants delegated new-ticket permission. Its contact selection, email selection and retry rules are separate.',
    ),
    terminalNote,
  ],
};

export const BUSINESS_FLOWS: BusinessFlow[] = [
  newCustomerFlow('a'),
  newCustomerFlow('b'),
  newCustomerFlow('c21'),
  c22Flow,
  existingFlow,
  closedFlow,
  callbackFlow,
  technicianFlow,
];
